/**
 * Defaults derivados de `countryCode` — fonte única para idioma e fusos
 * horários por país. Usado no onboarding e no form de filiais para
 * preencher/restringir esses campos sem deixar o usuário digitar livremente.
 *
 * Idioma é fixo por país. Fuso é derivado quando o país tem só um;
 * países com múltiplos fusos exibem dropdown limitado a essa lista.
 *
 * **Ao acrescentar um país ou um fuso aqui, o Android precisa ser atualizado à
 * mão**: `CountryTimeZones.kt` é cópia manual desta tabela e nada compara as
 * duas. O iOS não: desde a Fase 9C (#536) a lista entra no snapshot do contrato
 * (`tables.json` → `Contract/.../Tables.swift`) e o `contract:check` reprova se
 * sair de sincronia. Fechar essa lacuna do lado Kotlin é a #543.
 */
import type { Country } from './enums';
import type { TenantLocale } from './schemas';

export type CountryDefaults = {
  /** Locale do produto (lista fechada — alinha com `packages/i18n/locales`). */
  locale: TenantLocale;
  /** Fusos IANA suportados no país, em ordem de uso (mais comum primeiro). */
  timeZones: readonly string[];
};

const DEFAULTS: Readonly<Record<Country, CountryDefaults>> = {
  BR: {
    locale: 'pt-BR',
    timeZones: [
      'America/Sao_Paulo',
      'America/Bahia',
      'America/Fortaleza',
      'America/Recife',
      'America/Belem',
      'America/Maceio',
      'America/Araguaina',
      'America/Cuiaba',
      'America/Campo_Grande',
      'America/Manaus',
      'America/Porto_Velho',
      'America/Boa_Vista',
      'America/Rio_Branco',
      'America/Eirunepe',
      'America/Noronha',
    ],
  },
  MX: {
    locale: 'es-MX',
    timeZones: [
      'America/Mexico_City',
      'America/Monterrey',
      'America/Cancun',
      'America/Merida',
      'America/Chihuahua',
      'America/Hermosillo',
      'America/Mazatlan',
      'America/Tijuana',
      'America/Bahia_Banderas',
      'America/Matamoros',
      'America/Ojinaga',
    ],
  },
  US: {
    locale: 'en',
    timeZones: [
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Phoenix',
      'America/Anchorage',
      'Pacific/Honolulu',
    ],
  },
  CA: {
    locale: 'en',
    timeZones: [
      'America/Toronto',
      'America/Vancouver',
      'America/Edmonton',
      'America/Winnipeg',
      'America/Halifax',
      'America/St_Johns',
      'America/Regina',
    ],
  },
  IT: {
    locale: 'it',
    timeZones: ['Europe/Rome'],
  },
};

export function getCountryDefaults(country: Country): CountryDefaults {
  return DEFAULTS[country];
}

/**
 * Locale de formatação de um país. Desde a #942 (2026-10-12) aceita qualquer
 * ISO alpha-2: país fora dos 5 mercados (tenant do IAP, ex. `JP`) cai em `en`
 * em vez de lançar `undefined.locale`.
 */
export function localeForCountry(country: string): TenantLocale {
  const defaults: CountryDefaults | undefined = (
    DEFAULTS as Partial<Record<string, CountryDefaults>>
  )[country];
  return defaults?.locale ?? 'en';
}

export function timeZonesForCountry(country: Country): readonly string[] {
  return DEFAULTS[country].timeZones;
}

export function isTimeZoneSupported(country: Country, tz: string): boolean {
  return DEFAULTS[country].timeZones.includes(tz);
}

export function defaultTimeZoneForCountry(country: Country): string {
  // Cada país tem ao menos um fuso por construção em DEFAULTS.
  const [first] = DEFAULTS[country].timeZones;
  if (!first) {
    throw new Error(`countryDefaults: país ${country} sem fusos configurados`);
  }
  return first;
}
